<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="contact-form small-body-text">
    <div v-if="successMessageRef">
      <div class="contact__success mt-xs">
        <SvgSprite
          class="icon"
          symbol="ui-success"
          size="24"
          aria-hidden="true"
        />
        <span v-if="route.params.lang === 'en'">Successfully sent</span>
        <span v-else-if="route.params.lang === 'nl'">Succesvol verzonden</span>
        <span v-else>Envoi avec succès</span>
      </div>

      <HtmlText class="contact-form__message" :content="successMessageRef" />

      <div v-if="successExtraRef" class="contact-form__supported mt-xl">
        <div
          class="contact-form__supported__title mt-m"
          v-html="successExtraRef.title"
        ></div>

        <div class="contact-form__supported__list mt-s">
          <IndividualCta
            v-for="(item, index) in successExtraRef.items"
            :key="`supported-item-${index}`"
            :content="item"
          ></IndividualCta>
        </div>
      </div>
    </div>

    <div v-else>
      <FormBody
        ref="contactForm"
        class="contact__form"
        :content="content"
        :original-state="prefilledState"
        :turnstile-widget-selector="'#turnstile-widget'"
        @submit="onSubmit"
        @submit:success="onSubmitSuccess"
        @submit:fail="onSubmitFail"
      >
        <template #item-date="{ item, state }">
          <FormDate
            :item="item"
            :path="item.uid"
            :state="state"
            @emit-date="updateDate"
          />
        </template>

        <template #global-submit="{ loading }">
          <div
            id="turnstile-widget"
            class="contact__form__turnstile"
            :data-sitekey="turnstileKey"
          ></div>

          <Action
            :content="{
              tag: 'button',
              title: content.settings?.sendButtonLabel,
            }"
            :disabled="loading"
            :class="{ loading }"
            type="submit"
          />
        </template>
      </FormBody>

      <!-- <pre>{{ contactForm?.state }}</pre> -->
    </div>
  </div>
</template>

<script lang="ts">
import type { FormContent } from '@monofront/bob'
import type { AxiosResponse } from 'axios'
import type { PropType } from 'vue'
import type { Picture } from '@/types'

interface contact {
  picture: Picture
  title: string
  htmltext: string
}

interface successExtra {
  title: string
  items: contact[]
}
</script>

<script setup lang="ts">
import { FormBody } from '@monofront/bob'
import { onBeforeMount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import FormDate from '@/components/form/Date.vue'
import IndividualCta from '@/components/global/IndividualCta.vue'
import { push } from '@/utils/tracking'

const route = useRoute()

const props = defineProps({
  content: {
    type: Object as PropType<FormContent>,
    required: true,
  },
})

const contactForm = ref()
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const prefilledState: Record<string, any> = {}
const turnstileKey = import.meta.env.VITE_TURNSTILE_KEY as string
let turnstileInit = false

// Get fields values from query
const getPrefilledItems = () => {
  const form = props.content
  const { query } = route
  const subjetField = form.fields.find(
    field => field.name === Object.keys(query)[0]
  )

  if (subjetField) {
    prefilledState[subjetField.uid] = query.subject
  }
}

const updateDate = (path: string, data: Date) => {
  contactForm.value.state[path] = data.toLocaleDateString('en-CA')

  // Disable 13h + on Saturday
  if (data.getDay() === 6) {
    const hours = document.querySelectorAll('.form-checkboxgroup__choice')

    for (let i = 3; i < hours.length; i++) {
      const el = hours[i]
      el.classList.add('is-disabled')

      const input = el.querySelector('.form-element')
      input?.setAttribute('disabled', '')
    }
  } else {
    const hoursDisabled = document.querySelectorAll(
      '.form-checkboxgroup__choice.is-disabled'
    )

    hoursDisabled.forEach(el => {
      el.classList.remove('is-disabled')

      const input = el.querySelector('.form-element')
      input?.removeAttribute('disabled')
    })
  }
}

// Vars
const successMessageRef = ref<string | null>(null)
const successExtraRef = ref({} as successExtra)

onMounted(() => {
  initTurnstile()

  window.onloadTurnstileCallback = () => {
    initTurnstile()
  }
})

const initTurnstile = () => {
  if (window.turnstile && turnstileInit) {
    return
  }

  const success = window.turnstile.render('#turnstile-widget', {
    sitekey: turnstileKey,
  })

  if (success) {
    turnstileInit = true
  }
}

const onSubmit = (data: FormEntry[]) => {
  const formSubject = data.find(
    (entry: { name: string }) => entry.name === 'subject'
  )

  const newsletterOptin = data.find(
    (entry: { name: string }) => entry.name === 'newsletter'
  )

  push({
    event: 'contact_form_send',
    formSubject: formSubject.value,
    newsletterOptin: newsletterOptin.value,
  })
}

const onSubmitSuccess = (response?: AxiosResponse) => {
  if (!response || !response.data.message) {
    console.error('No success message', response)
    successMessageRef.value = null

    return
  }

  successMessageRef.value = response.data.message
  successExtraRef.value = response.data.extra?.contacts
}

const onSubmitFail = () => {
  console.log('contact: submit fail')
}

onBeforeMount(() => {
  getPrefilledItems()
})
</script>

<style lang="scss" scoped>
.contact-form {
  padding: 0;

  :deep(.body__validations) {
    margin-top: $spacing * 1.5;
    font-size: 1.4rem;
  }
}

.contact__title {
  @extend %fw-bold;
}

.contact__text {
  color: $c-grey-dark;
}

.contact__form {
  --form-spacing: 0.5rem;

  font-size: 2rem;

  :deep(.form-title) {
    @extend %fraunces-spec;
    @extend %fw-light;

    font-size: 3.8rem;
    line-height: 1;
  }
}

.contact__success {
  display: flex;
  align-items: center;
  width: fit-content;
  padding: 0.8rem 1.8rem 0.8rem 1.2rem;
  line-height: 1;
  background-color: $c-success-light;
  border-radius: $spacing;

  .icon {
    margin-right: 0.8rem;
    fill: $c-success;
  }
}

.contact-form__supported {
  border-top: 1px solid $c-grey-light;
}

.contact-form__supported__title {
  @extend %fw-semi;

  font-size: 1.6rem;
  line-height: 2.2rem;
}

.contact-form__supported__list {
  position: relative;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: $spacing;

  @include mq(xl) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.contact-form__message {
  margin-top: 3.6rem;

  :deep(p) {
    margin-bottom: 1.6rem;
  }

  :deep(strong) {
    @extend %fraunces-spec;
    @extend %fw-light;

    font-size: 3.8rem;
    line-height: 4.8rem;
  }

  :deep(ul) {
    @extend %list-nostyle;

    margin-top: 4.8rem;
    color: $c-grey-dark;
  }

  :deep(li) {
    &::before {
      display: none;
    }
  }
}

.contact__form__turnstile {
  margin-bottom: $spacing * 2;
}
</style>
